// Performance-Optimized ClientList.js
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import SearchBar from '../components/SearchBar';
import { calculateClientQuality } from '../utils/clientDataValidator';
import '../styles/ClientList.css';
import { useAppData } from '../context/AppDataContext';

export default function ClientList() {
  const { clients: cachedClients, loaded } = useAppData();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [filterBy, setFilterBy] = useState('all');
  const [showDataQuality, setShowDataQuality] = useState(false);

  // PERFORMANCE FIX 1: Memoize quality scores to avoid recalculation
  const clientsWithQuality = useMemo(() => {
    return clients.map(client => ({
      ...client,
      qualityScore: calculateClientQuality(client),
      searchText: `${client.name || ''} ${client.city || ''} ${client.state || ''} ${client.manufacturer || ''}`.toLowerCase()
    }));
  }, [clients]);

  // PERFORMANCE FIX 2: Memoize expensive computations
  const incompleteProfiles = useMemo(() => {
    if (clients.length === 0) return [];
    return clientsWithQuality.filter(c => c.qualityScore < 80);
  }, [clients, clientsWithQuality]);

  // PERFORMANCE FIX 3: Debounced search for better responsiveness
  const [debouncedSearch, setDebouncedSearch] = useState('');
  
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 150); // 150ms debounce
    
    return () => clearTimeout(timer);
  }, [search]);

  // PERFORMANCE FIX 4: Optimized filtering and sorting with memoization
  const filteredAndSortedClients = useMemo(() => {
    const searchTerm = debouncedSearch.toLowerCase();
    
    // Filter clients
    let filtered = clientsWithQuality.filter(client => {
      // Fast search using pre-computed searchText
      const matchesSearch = !searchTerm || client.searchText.includes(searchTerm);
      if (!matchesSearch) return false;
      
      // Filter by data quality using pre-computed score
      switch (filterBy) {
        case 'incomplete':
          return client.qualityScore < 80;
        case 'missing-contact':
          return !client.phone && !client.email;
        case 'missing-address':
          return !client.street1 || !client.city || !client.state;
        default:
          return true;
      }
    });

    // Sort clients (using pre-computed values where possible)
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
        case 'city':
          return (a.city || '').toLowerCase().localeCompare((b.city || '').toLowerCase());
        case 'state':
          return (a.state || '').toLowerCase().localeCompare((b.state || '').toLowerCase());
        case 'manufacturer':
          return (a.manufacturer || '').toLowerCase().localeCompare((b.manufacturer || '').toLowerCase());
        case 'quality':
          return b.qualityScore - a.qualityScore; // Pre-computed scores
        default:
          return 0;
      }
    });

    return filtered;
  }, [clientsWithQuality, debouncedSearch, filterBy, sortBy]);

  // Original CSV export function (unchanged for compatibility)
  const exportToCSV = useCallback(() => {
    if (clients.length === 0) return;
    
    const LOCAL_BASE = 'C:/Users/ITR/Strictly Results Dropbox/Assets/Scripts/firebase-client-logos';
    
    const getFileName = url => {
      if (!url) return '';
      try {
        const rawPath = new URL(url).pathname;
        const afterO = rawPath.split('/o/')[1] || '';
        const decoded = decodeURIComponent(afterO);
        return decoded.split('/').pop();
      } catch {
        return decodeURIComponent(url.split('/').pop().split('?')[0]);
      }
    };
    
    const toWinPath = posixPath => posixPath.replace(/\//g, '\\');
    const buildPath = (clientId, filename) => {
      if (!filename) return '';
      const posix = `${LOCAL_BASE}/${clientId}/${filename}`;
      return toWinPath(posix);
    };
    
    const headers = [
      'id','name', 'street1', 'city','state', 'zip', 'dealershipWebsite', 'generalManager', 'manufacturer',
      'dealershipHours', 'email', 'email2', 'phone', 'phone2', 'password', 'disclaimerLink', 'dealerAppsCode', '@blackLogoPath'
    ];
    
    const rows = clients.map(c => {
      const bf = getFileName(c.blackLogoUrl);
      return [
        c.id, c.name || '', c.street1 || '', c.city || '', c.state || '', c.zip || '',
        c.dealershipWebsite || '', c.generalManager || '', c.manufacturer || '',
        c.dealershipHours || '', c.email || '', c.email2 || '', c.phone || '',
        c.phone2 || '', c.password || '', c.disclaimerLink || '', c.dealerAppsCode || '',
        buildPath(c.id, bf),
      ];
    });
    
    const csvContent = [headers.join(','), ...rows.map(r => r.map(v => `"${v}"`).join(','))].join('\r\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'Master Dealership Spreadsheet.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [clients]);

useEffect(() => {
  if (!loaded) return;
  const sorted = [...cachedClients].sort((a, b) =>
    (a.name || '').toLowerCase().localeCompare((b.name || '').toLowerCase())
  );
  setClients(sorted);
  setLoading(false);
}, [loaded, cachedClients]);

  return (
    <div className="client-page">
      {/* Header */}
      <header className="client-header">
        <h2 className="gradient-text" style={{ margin: '0 0 44px 0', alignItems: 'center', display: 'flex', flexDirection: 'column'}}>
          Client Management
        </h2>

        {/* Enhanced Search and Filters */}
        <div className="client-search-row">
          <SearchBar
            placeholder="🔍 Search anything..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>

        {/* Controls */}
        <div className="client-controls" style={{ display: 'flex', gap: '10px', margin: '10px 0', flexWrap: 'wrap', justifyContent: 'center' }}>
          <select value={sortBy} onChange={e => setSortBy(e.target.value)} style={{ padding: '8px 12px', borderRadius: '4px', border: '1px solid #ccc' }}>
            <option value="name">Sort by Name</option>
            <option value="city">Sort by City</option>
            <option value="state">Sort by State</option>
            <option value="manufacturer">Sort by Manufacturer</option>
            <option value="quality">Sort by Data Quality</option>
          </select>
          
          <select value={filterBy} onChange={e => setFilterBy(e.target.value)} style={{ padding: '8px 12px', borderRadius: '4px', border: '1px solid #ccc' }}>
            <option value="all">Show All</option>
            <option value="incomplete">Incomplete Profiles (&lt;80%)</option>
            <option value="missing-contact">Missing Contact Info</option>
            <option value="missing-address">Missing Address</option>
          </select>
          
          <button 
            onClick={() => setShowDataQuality(!showDataQuality)}
            style={{ padding: '8px 12px', borderRadius: '4px', border: '1px solid #ccc', backgroundColor: showDataQuality ? '#007bff' : '#f8f9fa', color: showDataQuality ? 'white' : 'black' }}
          >
            {showDataQuality ? 'Hide' : 'Show'} Data Quality
          </button>
        </div>

        {/* Action Buttons */}
        <div className="client-actions">
          <Link to="/clients/new">
            <button>Add New Client</button>
          </Link>
          <button onClick={exportToCSV} style={{ marginLeft: 16 }}>
            Export Clients
          </button>
        </div>
      </header>

      {/* Data Quality Summary */}
      {showDataQuality && (
        <div style={{ backgroundColor: '#f8f9fa', padding: '15px', borderRadius: '8px', margin: '10px 0' }}>
          <h4>Data Quality Summary</h4>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '10px' }}>
            <div><strong>Total Clients:</strong> {clients.length}</div>
            <div><strong>Incomplete Profiles:</strong> {incompleteProfiles.length} ({clients.length > 0 ? Math.round((incompleteProfiles.length / clients.length) * 100) : 0}%)</div>
            <div><strong>Missing Contact Info:</strong> {clientsWithQuality.filter(c => !c.phone && !c.email).length}</div>
          </div>
        </div>
      )}

      {/* Client Count */}
      {!loading && (
        <p style={{ marginTop: '10px', marginBottom: '10px' }}>
          Showing {filteredAndSortedClients.length} of {clients.length} clients
          {filterBy !== 'all' && <span> (filtered)</span>}
        </p>
      )}

      {/* Loading / Empty States */}
      {loading ? (
        <p>Loading clients…</p>
      ) : filteredAndSortedClients.length === 0 ? (
        <p>No clients match your search criteria.</p>
      ) : (
        /* Client List */
        <ul className="client-list">
          <AnimatePresence>
            {filteredAndSortedClients.map((client) => {
              const isIncomplete = client.qualityScore < 80;
              
              return (
                <motion.li 
                  key={client.id} 
                  className={`client-item ${isIncomplete ? 'incomplete-profile' : ''}`}
                  style={{
                    border: isIncomplete ? '2px solid #ffc107' : undefined,
                    position: 'relative'
                  }}
                >
                  {showDataQuality && (
                    <div style={{ 
                      position: 'absolute', 
                      top: '10px', 
                      right: '10px', 
                      backgroundColor: client.qualityScore >= 80 ? '#28a745' : client.qualityScore >= 60 ? '#ffc107' : '#dc3545',
                      color: 'white',
                      padding: '2px 6px',
                      borderRadius: '12px',
                      fontSize: '0.8rem',
                      fontWeight: 'bold'
                    }}>
                      {client.qualityScore}%
                    </div>
                  )}
                  
                  <strong className="client-name">
                    {client.name || 'Unnamed Client'}
                    {isIncomplete && <span style={{ color: '#ffc107', marginLeft: '5px' }}>⚠️</span>}
                  </strong>
                  
                  {(client.city || client.state) && (
                    <span className="client-location">
                      {client.city && client.state ? `${client.city}, ${client.state}` : client.city || client.state}
                    </span>
                  )}
                  
                  {client.manufacturer && (
                    <span className="client-manufacturer">
                      {client.manufacturer}
                    </span>
                  )}
                  
                  {showDataQuality && (
                    <div style={{ fontSize: '0.8rem', color: '#666', marginTop: '5px' }}>
                      {!client.phone && !client.email && <span style={{ color: '#dc3545' }}>Missing contact info</span>}
                      {(!client.street1 || !client.city || !client.state) && <span style={{ color: '#dc3545' }}>{(!client.phone && !client.email) ? ', ' : ''}Incomplete address</span>}
                    </div>
                  )}
                  
                  <div className="client-actions">
                    <Link to={`/clients/${client.id}`}>
                      <button>🔍 View/Edit</button>
                    </Link>
                  </div>
                </motion.li>
              );
            })}
          </AnimatePresence>
        </ul>
      )}
    </div>
  );
}